// Gallery - fullscreen lightbox over a list of images.
//
// Replaces v1's separate gallery bundle (app/styles/gallery.scss +
// app/scripts/gallery.js), which was nothing but a re-export of the archived
// lightgallery.js 1.x plus its vendor CSS; every consumer page then wired it up
// with its own inline script. Here the behavior ships in the main bundle and is
// fully declarative - see docs/porting.md for the deliberate deviations.
//
// HTML contract - thumbnails visible in the page, click opens that image:
//   <div class="gallery gallery--small" data-ms-gallery>
//     <a class="gallery__item" href="{full}" data-ms-gallery-caption="Popis">
//       <img src="{thumb}" alt="Popis">
//     </a>
//   </div>
//
// Opening from an unrelated control (v1 did this with the id <-> data-button
// pair): keep the item list in the page hidden and point a trigger at it.
//   <button type="button" data-ms-gallery-open="#store-photos">Fotky</button>
//   <div class="gallery hide" id="store-photos" data-ms-gallery>...</div>
//
// Per-gallery overrides (on the container): data-ms-gallery-thumbnails,
// -counter, -loop, -zoom, all "true"/"false".
// Per-trigger: data-ms-gallery-index="2" opens at that item (default 0).
// Per-item: data-ms-gallery-src (full-size URL if not the href),
// data-ms-gallery-thumb (thumbnail URL if not the nested img),
// data-ms-gallery-caption.

import Swiper from 'swiper';
import { Keyboard, Navigation, Thumbs, Zoom } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';

import { lockBody, unlockBody } from './modal';

export type GalleryLanguage = 'sk' | 'cz';

export interface GalleryTexts {
  dialog: string;
  close: string;
  prev: string;
  next: string;
}

export interface GalleryConfig {
  /** Render the thumbnail strip below the image. */
  thumbnails?: boolean;
  /** Render the "3 / 12" counter in the toolbar. */
  counter?: boolean;
  /** Arrow keys move between images. */
  keyboard?: boolean;
  /** Wrap around at the ends. */
  loop?: boolean;
  /** Allow pinch/double-tap zoom on the image. Off in v1. */
  zoom?: boolean;
  /** Close on a click outside the image. */
  closeOnBackdrop?: boolean;
  /** Close on Escape. */
  closeOnEscape?: boolean;
  /** Class added to <body> while the lightbox is open. */
  bodyClass?: string;
  /** UI language for aria-labels; defaults to <html lang>. */
  language?: GalleryLanguage;
  /** Selector of the openable items inside a gallery container. */
  itemSelector?: string;
}

/** One image collected from the container's markup. */
export interface GalleryItem {
  src: string;
  thumb: string;
  alt: string;
  caption: string;
}

// Must match $lightbox-transition-duration in styles/modules/_lightbox.scss.
const TRANSITION_MS = 200;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const texts = {
  sk: {
    dialog: 'Galéria fotiek',
    close: 'Zavrieť galériu',
    prev: 'Predchádzajúca fotka',
    next: 'Ďalšia fotka',
  },
  cz: {
    dialog: 'Galerie fotek',
    close: 'Zavřít galerii',
    prev: 'Předchozí fotka',
    next: 'Další fotka',
  },
} satisfies Record<GalleryLanguage, GalleryTexts>;

/** Resolve the default language from <html lang>, as in modules/select.ts. */
const detectLanguage = (): GalleryLanguage => {
  const lang = document.documentElement.lang.toLowerCase();
  return lang.startsWith('cs') || lang.startsWith('cz') ? 'cz' : 'sk';
};

const defaultConfig: GalleryConfig = {
  thumbnails: true,
  counter: true,
  keyboard: true,
  loop: true,
  zoom: false,
  closeOnBackdrop: true,
  closeOnEscape: true,
  itemSelector: 'a[href]',
};

/** Read a "true"/"false" data attribute, falling back to the config value. */
const boolAttr = (element: HTMLElement, name: string, fallback: boolean): boolean => {
  const value = element.getAttribute(name);
  if (value === null || value === '') return fallback;
  return value !== 'false';
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export default class Gallery {
  selector: string;

  config: GalleryConfig;

  texts: GalleryTexts;

  /** Gallery containers found on init. */
  galleries: HTMLElement[] = [];

  private triggers: HTMLElement[] = [];

  private overlay: HTMLElement | null = null;

  private stage: Swiper | null = null;

  private thumbs: Swiper | null = null;

  private openGallery: HTMLElement | null = null;

  private lastFocused: HTMLElement | null = null;

  private closeTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(selector = '[data-ms-gallery]', config: GalleryConfig = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };
    this.texts = texts[this.config.language ?? detectLanguage()];

    this._init();
  }

  /** Open a gallery at the given index. Accepts the container or a selector. */
  open(target: HTMLElement | string, index = 0): void {
    const gallery =
      typeof target === 'string' ? document.querySelector<HTMLElement>(target) : target;

    if (!gallery) {
      console.error(`Gallery: container "${String(target)}" not found`);
      return;
    }

    const items = this.getItems(gallery);
    if (items.length === 0) return;

    clearTimeout(this.closeTimer);
    this._destroySwipers();

    this.lastFocused = document.activeElement as HTMLElement | null;
    this.openGallery = gallery;

    const withThumbs = boolAttr(gallery, 'data-ms-gallery-thumbnails', !!this.config.thumbnails)
      && items.length > 1;
    const withCounter = boolAttr(gallery, 'data-ms-gallery-counter', !!this.config.counter)
      && items.length > 1;
    const withZoom = boolAttr(gallery, 'data-ms-gallery-zoom', !!this.config.zoom);
    const loop = boolAttr(gallery, 'data-ms-gallery-loop', !!this.config.loop) && items.length > 1;

    const overlay = this._ensureOverlay();
    overlay.innerHTML = this._render(items, { withThumbs, withCounter, initialIndex: index });

    // Reduced motion: keep the slide change instant. Same intent - and the same
    // optional call, for jsdom - as in modules/smooth-scroll.ts.
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (withThumbs) {
      this.thumbs = new Swiper(overlay.querySelector('.lightbox__thumbs') as HTMLElement, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        // Center the strip only while it is short enough to fit; once it
        // overflows, the Thumbs module scrolls the active thumb into view and
        // centering would fight it.
        centerInsufficientSlides: true,
        slideToClickedSlide: true,
        watchSlidesProgress: true,
      });
    }

    const stageOptions: SwiperOptions = {
      initialSlide: index,
      loop,
      speed: reducedMotion ? 0 : 300,
      // A11y note: the overlay itself is the dialog and the toolbar carries the
      // labelled controls, so Swiper's own a11y layer would only add a second,
      // redundant live region on top of our counter.
      a11y: false,
      navigation: {
        prevEl: overlay.querySelector<HTMLElement>('.lightbox__btn--prev'),
        nextEl: overlay.querySelector<HTMLElement>('.lightbox__btn--next'),
      },
      keyboard: {
        enabled: this.config.keyboard !== false,
        // Only while the lightbox has focus - otherwise Swiper keeps listening
        // after close and steals the arrow keys from the page.
        onlyInViewport: true,
      },
      modules: [Navigation, Keyboard, ...(withThumbs ? [Thumbs] : []), ...(withZoom ? [Zoom] : [])],
      ...(withThumbs && this.thumbs ? { thumbs: { swiper: this.thumbs } } : {}),
      ...(withZoom ? { zoom: { maxRatio: 3 } } : {}),
    };

    this.stage = new Swiper(overlay.querySelector('.lightbox__stage') as HTMLElement, stageOptions);

    if (withCounter) {
      this.stage.on('slideChange', this._updateCounter);
      this._updateCounter();
    }

    lockBody(this.config.bodyClass);

    // Two layout flushes for the same reason as in modules/popover.ts: the
    // overlay must be laid out before [data-state] flips (so the fade runs) and
    // visible before focus() lands, because focus() is a silent no-op on an
    // element the browser still considers invisible.
    void overlay.offsetHeight;
    overlay.setAttribute('data-state', 'visible');
    void overlay.offsetHeight;

    (overlay.querySelector<HTMLElement>('.lightbox__close') ?? overlay).focus();

    document.addEventListener('keydown', this._onKeydown);
  }

  /** Close the lightbox. No-op when it is not open. */
  close(): void {
    const { overlay } = this;
    if (!overlay || !this.openGallery) return;

    overlay.setAttribute('data-state', 'hidden');
    document.removeEventListener('keydown', this._onKeydown);
    unlockBody(this.config.bodyClass);

    // Focus goes back to whatever opened the gallery - the thumbnail or the
    // external trigger.
    if (overlay.contains(document.activeElement)) {
      this.lastFocused?.focus();
    }
    this.lastFocused = null;
    this.openGallery = null;

    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this._destroySwipers();
      overlay.innerHTML = '';
    }, TRANSITION_MS);
  }

  /** Is the lightbox currently open? */
  isOpen(): boolean {
    return this.openGallery !== null;
  }

  next(): void {
    this.stage?.slideNext();
  }

  prev(): void {
    this.stage?.slidePrev();
  }

  /** Collect the images of a gallery container from its markup. */
  getItems(gallery: HTMLElement): GalleryItem[] {
    const selector = this.config.itemSelector ?? 'a[href]';

    return [...gallery.querySelectorAll<HTMLElement>(selector)].reduce<GalleryItem[]>(
      (items, item) => {
        const image = item.querySelector('img');
        const src =
          item.getAttribute('data-ms-gallery-src')
          ?? item.getAttribute('href')
          ?? image?.getAttribute('src')
          ?? '';

        if (!src) return items;

        const alt = image?.getAttribute('alt') ?? '';

        items.push({
          src,
          thumb: item.getAttribute('data-ms-gallery-thumb') ?? image?.getAttribute('src') ?? src,
          alt,
          caption: item.getAttribute('data-ms-gallery-caption') ?? '',
        });

        return items;
      },
      [],
    );
  }

  /** Re-scan the DOM for galleries and triggers added since init. */
  update(): void {
    this._teardownListeners();
    this._init();
  }

  destroy(): void {
    this.close();
    clearTimeout(this.closeTimer);
    this._destroySwipers();
    this._teardownListeners();

    this.overlay?.remove();
    this.overlay = null;
    this.openGallery = null;
  }

  private _init(): void {
    this.galleries = [...document.querySelectorAll<HTMLElement>(this.selector)];
    this.galleries.forEach(gallery => {
      gallery.addEventListener('click', this._onGalleryClick);
    });

    this.triggers = [...document.querySelectorAll<HTMLElement>('[data-ms-gallery-open]')];
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', this._onTriggerClick);
    });
  }

  private _teardownListeners(): void {
    this.galleries.forEach(gallery => {
      gallery.removeEventListener('click', this._onGalleryClick);
    });
    this.galleries = [];

    this.triggers.forEach(trigger => {
      trigger.removeEventListener('click', this._onTriggerClick);
    });
    this.triggers = [];
  }

  private _destroySwipers(): void {
    // The stage holds a reference to the thumbs instance, so it goes first.
    this.stage?.destroy(true, false);
    this.stage = null;
    this.thumbs?.destroy(true, false);
    this.thumbs = null;
  }

  private _ensureOverlay(): HTMLElement {
    if (this.overlay) return this.overlay;

    const overlay = document.createElement('div');
    overlay.className = 'lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', this.texts.dialog);
    overlay.setAttribute('data-state', 'hidden');
    overlay.setAttribute('tabindex', '-1');
    overlay.addEventListener('click', this._onOverlayClick);

    document.body.append(overlay);
    this.overlay = overlay;

    return overlay;
  }

  private _render(
    items: GalleryItem[],
    { withThumbs, withCounter, initialIndex }: {
      withThumbs: boolean;
      withCounter: boolean;
      initialIndex: number;
    },
  ): string {
    const slides = items
      .map((item, i) => {
        const caption = item.caption
          ? `<figcaption class="lightbox__caption">${escapeHtml(item.caption)}</figcaption>`
          : '';

        // .swiper-zoom-container is what the Swiper Zoom module hooks into; the
        // vendor CSS also centers and contains the image inside it, so it is
        // rendered whether or not zoom is enabled. Everything but the first
        // image loads lazily - galleries of 20+ store photos are common.
        return `<figure class="lightbox__slide swiper-slide">
          <div class="swiper-zoom-container">
            <img class="lightbox__image" src="${escapeHtml(item.src)}"
              alt="${escapeHtml(item.alt)}"${i === initialIndex ? '' : ' loading="lazy"'}>
          </div>
          ${caption}
        </figure>`;
      })
      .join('\n');

    const thumbs = withThumbs
      ? `<div class="swiper lightbox__thumbs">
          <div class="swiper-wrapper">
            ${items
              .map(
                item => `<div class="swiper-slide">
                  <img class="lightbox__thumb" src="${escapeHtml(item.thumb)}" alt=""
                    loading="lazy">
                </div>`,
              )
              .join('\n')}
          </div>
        </div>`
      : '';

    // aria-live on the counter announces the position after each slide change;
    // that is why Swiper's own a11y module stays off (see stageOptions).
    const counter = withCounter
      ? `<p class="lightbox__counter" aria-live="polite">
          <span class="lightbox__counter-current">${initialIndex + 1}</span> / ${items.length}
        </p>`
      : '';

    const nav =
      items.length > 1
        ? `<button class="lightbox__btn lightbox__btn--prev" type="button"
            aria-label="${escapeHtml(this.texts.prev)}">
            <i class="far fa-chevron-left fa-2x" aria-hidden="true"></i>
          </button>
          <button class="lightbox__btn lightbox__btn--next" type="button"
            aria-label="${escapeHtml(this.texts.next)}">
            <i class="far fa-chevron-right fa-2x" aria-hidden="true"></i>
          </button>`
        : '';

    return `<div class="lightbox__backdrop" data-ms-gallery-close></div>
      <div class="lightbox__dialog">
        <div class="lightbox__toolbar">
          ${counter}
          <button class="lightbox__close" type="button" data-ms-gallery-close
            aria-label="${escapeHtml(this.texts.close)}">
            <i class="far fa-xmark fa-2x" aria-hidden="true"></i>
          </button>
        </div>
        <div class="swiper lightbox__stage">
          <div class="swiper-wrapper">${slides}</div>
          ${nav}
        </div>
        ${thumbs}
      </div>`;
  }

  private _updateCounter = (): void => {
    const current = this.overlay?.querySelector('.lightbox__counter-current');
    // realIndex, not activeIndex: with loop enabled Swiper prepends duplicate
    // slides and activeIndex counts those too.
    if (current && this.stage) {
      current.textContent = String(this.stage.realIndex + 1);
    }
  };

  private _onGalleryClick = (event: MouseEvent): void => {
    const gallery = event.currentTarget as HTMLElement;
    const item = (event.target as Element | null)?.closest<HTMLElement>(
      this.config.itemSelector ?? 'a[href]',
    );

    if (!item || !gallery.contains(item)) return;

    // Let modified clicks through - the href is the full-size image, so
    // ctrl/cmd-click opening it in a new tab is a reasonable expectation.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    this.open(gallery, this.getItems(gallery).findIndex(({ src }) => src === this._itemSrc(item)));
  };

  private _onTriggerClick = (event: MouseEvent): void => {
    const trigger = event.currentTarget as HTMLElement;
    const selector = trigger.getAttribute('data-ms-gallery-open');
    const gallery = selector ? document.querySelector<HTMLElement>(selector) : null;

    if (!gallery) {
      console.error(`Gallery: container "${selector ?? ''}" not found for`, trigger);
      return;
    }

    event.preventDefault();
    this.open(gallery, Number(trigger.getAttribute('data-ms-gallery-index') ?? 0) || 0);
  };

  private _onOverlayClick = (event: MouseEvent): void => {
    const target = event.target as Element | null;

    if (target?.closest('[data-ms-gallery-close]')) {
      // The backdrop covers the whole overlay, so a click anywhere outside the
      // dialog lands on it - gate that on closeOnBackdrop, but always honour
      // the explicit close button.
      const isBackdrop = !target.closest('.lightbox__close');
      if (isBackdrop && this.config.closeOnBackdrop === false) return;

      event.preventDefault();
      this.close();
    }
  };

  private _onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.config.closeOnEscape !== false) {
      this.close();
      return;
    }

    if (event.key === 'Tab') this._trapFocus(event);
  };

  /** Keep Tab inside the overlay - it is aria-modal. */
  private _trapFocus(event: KeyboardEvent): void {
    const { overlay } = this;
    if (!overlay) return;

    const focusable = [...overlay.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (focusable.length === 0) {
      event.preventDefault();
      overlay.focus();
      return;
    }

    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !overlay.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !overlay.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  private _itemSrc(item: HTMLElement): string {
    return (
      item.getAttribute('data-ms-gallery-src')
      ?? item.getAttribute('href')
      ?? item.querySelector('img')?.getAttribute('src')
      ?? ''
    );
  }
}
